import React, { useState } from "react";
import { ApprovalResult } from "./OverCreditPermission";
import CommonButtons from "src/shared/components/CommonButtons";

interface AdminOwnerBasedProps {
  currentLimit?: number;
  onConfirm: (result: ApprovalResult) => void;
  onCancel: () => void;
  loading?: boolean;
}

function AdminOwnerBased({ currentLimit = 0, onConfirm, onCancel, loading = false }: AdminOwnerBasedProps) {
  const [showIncreaseInput, setShowIncreaseInput] = useState(false);
  const [newLimit, setNewLimit] = useState<string>(currentLimit ? String(currentLimit) : "");

  const parsedLimit = Number(newLimit);
  const isValidLimit = newLimit.trim() !== "" && !Number.isNaN(parsedLimit) && parsedLimit > 0;

  const handleApprove = () => {
    if (loading) return;
    onConfirm({ type: "approve" });
  };

  const handleIncreaseConfirm = () => {
    if (loading || !isValidLimit) return;
    onConfirm({ type: "increase-credit", newLimit: parsedLimit });
  };

  // ── Increase Limit Sub-View ──────────────────────────────
  if (showIncreaseInput) {
    return (
      <div>
        <div className="mb-4">
          <h3 className="text-base font-semibold text-[#14181F]">Increase Credit Limit</h3>
          <p className="text-xs text-[#5B6472] mt-1">Enter the new credit limit for this customer, then approve the order.</p>
        </div>

        <label className="block text-xs font-medium text-[#5B6472] mb-1">New credit limit</label>
        <input
          type="number"
          autoFocus
          min={0}
          value={newLimit}
          onChange={(e) => setNewLimit(e.target.value)}
          disabled={loading}
          placeholder={currentLimit ? `Current: ${currentLimit}` : "Enter amount"}
          className="w-full border border-[#E3E6EB] rounded-md px-3 py-2 text-sm text-[#14181F] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
        />

        <div className="flex justify-end gap-2 mt-5">
          <CommonButtons label="Back" variant="outlineDanger" onClick={() => setShowIncreaseInput(false)} disabled={loading} className="px-4 py-2" />
          <CommonButtons
            label={loading ? "Confirming..." : "Confirm"}
            variant="primary"
            onClick={handleIncreaseConfirm}
            disabled={loading || !isValidLimit}
            loading={loading}
            className="px-4 py-2 min-w-[110px]"
          />
        </div>
      </div>
    );
  }

  // ── Main Action List ─────────────────────────────────────
  return (
    <div>
      {/* Header with warning icon (from Version 2) */}
      <div className="flex items-start gap-3 mb-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
          <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900">Over Credit Limit</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Current limit: <span className="font-medium">{currentLimit}</span>. You can approve this order as-is or increase the credit limit.
          </p>
        </div>
      </div>

      {/* Action options */}
      <div className="space-y-2">
        <ActionOption label="Approve this order" description="Confirms the order as-is." onClick={handleApprove} disabled={loading} />
        <ActionOption label="Increase credit limit" description="Raise the customer's credit limit, then approve." onClick={() => setShowIncreaseInput(true)} disabled={loading} />
      </div>

      {/* Cancel */}
      <div className="flex justify-end mt-4">
        <CommonButtons label="Cancel" variant="outlineDanger" onClick={onCancel} disabled={loading} className="px-4 py-2" />
      </div>
    </div>
  );
}

export default AdminOwnerBased;

// ── ActionOption ───────────────────────────────────────────
function ActionOption({
  label,
  description,
  onClick,
  tone = "default",
  disabled = false,
}: {
  label: string;
  description: string;
  onClick: () => void;
  tone?: "default" | "danger";
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full text-left border rounded-md px-3 py-2.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        tone === "danger" ? "border-[#F4D3D0] hover:bg-[#FCEAE9]" : "border-[#E3E6EB] hover:bg-[#F6F7F9]"
      }`}
    >
      <div className={`text-sm font-medium ${tone === "danger" ? "text-[#B3261E]" : "text-[#14181F]"}`}>{label}</div>
      <div className="text-xs text-[#5B6472]">{description}</div>
    </button>
  );
}
